import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';

import { ClockCircleOutlined } from '@ant-design/icons';
import DegreesToCardinal from '../../parameter/DegreesToCardinal';
import appConfig, { brandingConfig } from '../../../config/index';
import MarkerStatus from '../../icons/MarkerStatus';

const ExternalDatasetSummary = (props) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { info, parameters, obsLatestData } = props;
  const { properties } = info;

  const datasetObservations = Array.isArray(obsLatestData)
    ? obsLatestData.find((data) => data.ext_obs_dataset_id === properties.ext_obs_dataset_id)
    : undefined;

  if (!datasetObservations) return null;

  const allTimestamps = datasetObservations?.parameters?.flatMap((p) => p.observations?.map((obs) => obs.timestamp) || []) || [];
  const latestTimestamp = allTimestamps.length > 0 ? Math.max(...allTimestamps) : null;

  const getStatus = (status) => {
    switch (status) {
      case 'activated':
        return 'active';
      case 'deactivated':
        return 'inactive';
      default:
        return status;
    }
  };

  const desiredOrder = ['Air temperature', 'Surface water temperature', 'Wind speed', 'Wind direction (from)', 'Air pressure at mean sea level', 'Significant wave height', 'Wave direction (from)', 'Dominant wave period', 'Chlorophyll fluorescence', 'Turbidity', 'pH', 'Phycocyanin fluorescence'];
  const getPriority = (name) => (desiredOrder.indexOf(name) >= 0 ? desiredOrder.indexOf(name) : 100);
  const platformEvent = properties.platform_event
    ? `This platform is ${getStatus(properties.platform_event)} and not ${properties.last_updated_parameter_in_hours > appConfig.oldDataCutoffInHours ? 'sending us' : 'reporting'} data` : 'There is no reporting data';

  return (
    <div>
      <div className="d-flex">
        <span className="h3" style={{ marginTop: 5, marginBottom: 5 }}>
          <div>{properties.org_platform_id}</div>
          {properties.platform_name}
          {latestTimestamp !== null && (
            <div
              className="caption-w"
              style={{ color: brandingConfig.colors.greyLight }}
            >
              <ClockCircleOutlined />
              &nbsp;
              {moment.unix(latestTimestamp).fromNow()}
            </div>
          )}
        </span>
        <span>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <MarkerStatus
              properties={properties}
              isFavorite={false} // favorites not supported for external datasets at this time
              iconProps={{
                height: '19px',
                width: '19px',
              }}
            />
          </span>
        </span>
      </div>

      <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
        {parameters.length > 0
          && properties.last_updated_parameter_in_hours <= appConfig.oldDataCutoffInHours
          ? parameters.sort((a, b) => (getPriority(a.name) - getPriority(b.name))).slice(0, 4).map((parameter, index) => (
            <Col key={index} span={isMobile ? 6 : 12}>
              <div className="h3">
                {DegreesToCardinal(parameter.value, parameter.unit)}
              </div>
              <div>
                {parameter.name}
              </div>
            </Col>
          )) : platformEvent}
      </Row>
    </div>
  );
};

ExternalDatasetSummary.propTypes = {
  info: PropTypes.object,
  parameters: PropTypes.array,
  obsLatestData: PropTypes.object,
};

ExternalDatasetSummary.defaultProps = {
  info: {},
  parameters: [],
  obsLatestData: {},
};

export default memo(ExternalDatasetSummary);
